import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, X } from "lucide-react";
import toast from "react-hot-toast";

import AddressAutocomplete from "../../../components/common/AddressAutocomplete";
import { createPropertyMarketUpdate } from "../api/prospectingReportsApi";
import { MU_RED, inputClass, outlineButtonClass, redButtonClass } from "../marketUpdateTheme";

const AU_STATES = ["VIC", "NSW", "QLD", "SA", "WA", "TAS", "NT", "ACT"];

// "60 Kernan Street, Strathmore VIC 3041" -> { suburb, state, postcode }
const partsFromAddress = (text) => {
  const pieces = String(text || "").split(",").map((s) => s.trim()).filter(Boolean);
  if (pieces.length < 2) return {};
  const tail = pieces[pieces.length - 1];
  const match = tail.match(/^(.*?)\s+(VIC|NSW|QLD|SA|WA|TAS|NT|ACT)\b\s*(\d{4})?/i);
  if (!match) return { suburb: tail };
  return { suburb: match[1], state: match[2].toUpperCase(), postcode: match[3] || "" };
};

/**
 * Start a market update for an owner who is not in the CRM yet. The vendor
 * details are filled in on the report's first step.
 */
const NewAddressReportModal = ({ onClose }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_address: "", suburb: "", state: "VIC", postcode: "" });
  const [saving, setSaving] = useState(false);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_address.trim() || !form.suburb.trim()) {
      toast.error("Enter the property address and its suburb.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await createPropertyMarketUpdate({ source_type: "manual", ...form });
      navigate(`/prospecting/market-update/${data.id}`);
    } catch (error) {
      const body = error?.response?.data;
      const detail = body && typeof body === "object" ? body : {};
      const fieldError = Object.values(detail)[0];
      toast.error(detail.detail || (Array.isArray(fieldError) ? fieldError[0] : "") || "Could not start the report.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form
        onSubmit={submit}
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-address-title"
        className="w-full max-w-lg rounded-xl bg-white shadow-[0_24px_48px_-12px_rgba(17,24,39,0.35)]"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 id="new-address-title" className="text-base font-bold text-gray-900">Market update for a new address</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-5 space-y-4">
          <label className="block">
            <span className="block text-sm font-semibold text-gray-800 mb-1.5">Property address</span>
            <AddressAutocomplete
              value={form.full_address}
              onChange={(e) => set("full_address", e.target.value)}
              onSelect={(suggestion) => {
                const desc = suggestion?.description || "";
                const parsed = { ...partsFromAddress(desc), ...(suggestion?.suburb ? { suburb: suggestion.suburb } : {}) };
                setForm((prev) => ({
                  ...prev,
                  full_address: desc || prev.full_address,
                  suburb: parsed.suburb || prev.suburb,
                  state: parsed.state || prev.state,
                  postcode: parsed.postcode || prev.postcode,
                }));
              }}
              placeholder="Start typing the address"
            />
          </label>
          <div className="grid grid-cols-[1fr_96px_96px] gap-3">
            <label className="block">
              <span className="block text-sm font-semibold text-gray-800 mb-1.5">Suburb</span>
              <input className={inputClass} value={form.suburb} onChange={(e) => set("suburb", e.target.value)} />
            </label>
            <label className="block">
              <span className="block text-sm font-semibold text-gray-800 mb-1.5">State</span>
              <select className={inputClass} value={form.state} onChange={(e) => set("state", e.target.value)}>
                {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-sm font-semibold text-gray-800 mb-1.5">Postcode</span>
              <input className={inputClass} value={form.postcode} inputMode="numeric" maxLength={4}
                onChange={(e) => set("postcode", e.target.value.replace(/\D/g, ""))} />
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button type="button" onClick={onClose} className={`${outlineButtonClass} px-4 py-2.5`}>Cancel</button>
          <button type="submit" disabled={saving} className={`${redButtonClass} px-5 py-2.5`} style={{ backgroundColor: MU_RED }}>
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Start report
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewAddressReportModal;
